import React, { useEffect, useRef, useState } from "react";
import { createPortal } from "react-dom";
import { ChevronDown } from "lucide-react";
import {
  DEFAULT_PHONE_COUNTRY_ISO2,
  PHONE_COUNTRIES,
  buildFullPhoneNumber,
  countryIsoToFlag,
  getPhoneCountryByIso2,
  parsePhoneNumber,
} from "../../constants/phoneCountries";
import "./PhoneCountryInput.css";

const PhoneCountryInput = ({ id, value = "", onChange, disabled = false }) => {
  const wrapperRef = useRef(null);
  const dropdownRef = useRef(null);
  const [dropdownOpen, setDropdownOpen] = useState(false);
  const [dropdownStyle, setDropdownStyle] = useState(null);
  const [selectedIso2, setSelectedIso2] = useState(DEFAULT_PHONE_COUNTRY_ISO2);
  const [localNumber, setLocalNumber] = useState("");

  useEffect(() => {
    const parsed = parsePhoneNumber(value, DEFAULT_PHONE_COUNTRY_ISO2);
    setSelectedIso2(parsed.iso2);
    setLocalNumber(parsed.local);
  }, []);

  useEffect(() => {
    const handleClickOutside = (event) => {
      if (wrapperRef.current?.contains(event.target)) return;
      if (dropdownRef.current?.contains(event.target)) return;
      setDropdownOpen(false);
    };

    document.addEventListener("mousedown", handleClickOutside);
    return () => document.removeEventListener("mousedown", handleClickOutside);
  }, []);

  useEffect(() => {
    if (!dropdownOpen || !wrapperRef.current) return;

    const updateDropdownPosition = () => {
      if (!wrapperRef.current) return;

      const rect = wrapperRef.current.getBoundingClientRect();
      const dropdownMaxHeight = 224;
      const gap = 6;
      const spaceBelow = window.innerHeight - rect.bottom;
      const openUpward = spaceBelow < dropdownMaxHeight + gap && rect.top > dropdownMaxHeight + gap;

      setDropdownStyle({
        position: "fixed",
        left: rect.left,
        width: rect.width,
        top: openUpward ? rect.top - dropdownMaxHeight - gap : rect.bottom + gap,
        maxHeight: dropdownMaxHeight,
        zIndex: 10002,
      });
    };

    updateDropdownPosition();
    window.addEventListener("resize", updateDropdownPosition);
    window.addEventListener("scroll", updateDropdownPosition, true);

    return () => {
      window.removeEventListener("resize", updateDropdownPosition);
      window.removeEventListener("scroll", updateDropdownPosition, true);
    };
  }, [dropdownOpen]);

  const selectedCountry = getPhoneCountryByIso2(selectedIso2);

  const updatePhone = (iso2, local) => {
    const country = getPhoneCountryByIso2(iso2);
    setSelectedIso2(country.iso2);
    setLocalNumber(local);
    onChange?.(buildFullPhoneNumber(country.dialCode, local));
  };

  const handleLocalChange = (event) => {
    const local = event.target.value.replace(/[^\d\s\-()]/g, "");
    updatePhone(selectedIso2, local);
  };

  const handleCountrySelect = (iso2) => {
    updatePhone(iso2, localNumber);
    setDropdownOpen(false);
  };

  return (
    <div
      ref={wrapperRef}
      className={`phone-country-input ${disabled ? "phone-country-input-disabled" : ""}`}
    >
      <div className="phone-country-input-row">
        <button
          type="button"
          className="phone-country-selector"
          onClick={() => !disabled && setDropdownOpen((open) => !open)}
          disabled={disabled}
          aria-label={`Country code ${selectedCountry.name}`}
        >
          <span className="phone-country-flag" aria-hidden="true">
            {countryIsoToFlag(selectedCountry.iso2)}
          </span>
          <span className="phone-country-code">{selectedCountry.dialCode}</span>
          <ChevronDown className={`phone-country-chevron ${dropdownOpen ? "open" : ""}`} />
        </button>

        <input
          id={id}
          type="tel"
          value={localNumber}
          onChange={handleLocalChange}
          placeholder="Phone number"
          autoComplete="tel-national"
          className="phone-country-number"
          disabled={disabled}
        />
      </div>

      {dropdownOpen &&
        dropdownStyle &&
        createPortal(
          <div
            ref={dropdownRef}
            className="phone-country-dropdown phone-country-dropdown-portal"
            style={dropdownStyle}
          >
            {PHONE_COUNTRIES.map((country) => (
              <button
                key={country.iso2}
                type="button"
                className={`phone-country-option ${
                  country.iso2 === selectedCountry.iso2 ? "selected" : ""
                }`}
                onClick={() => handleCountrySelect(country.iso2)}
              >
                <span className="phone-country-flag" aria-hidden="true">
                  {countryIsoToFlag(country.iso2)}
                </span>
                <span className="phone-country-option-name">{country.name}</span>
                <span className="phone-country-option-code">{country.dialCode}</span>
              </button>
            ))}
          </div>,
          document.body
        )}
    </div>
  );
};

export default PhoneCountryInput;
